import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, MapPin, Ticket, X, Bell, Clock, ArrowRight, ExternalLink, Share2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CASHAPP_QR = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/mq7wtatl_%24Badcompanyco-CASH%20APP%20QR.svg";
const VENMO_URL = "https://venmo.com/code?user_id=4592887459218935610&created=1778622181.1808228&printed=1";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

function RSVPModal({ event, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", ticket_preferences: [] });
  const [submitting, setSubmitting] = useState(false);

  const togglePref = (type) => {
    setForm(f => ({
      ...f,
      ticket_preferences: f.ticket_preferences.includes(type)
        ? f.ticket_preferences.filter(t => t !== type)
        : [...f.ticket_preferences, type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvps`, { ...form, event_id: event.id });
      toast.success("RSVP confirmed! We'll notify you when tickets are available.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to RSVP");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg border border-white/15 bg-zinc-950 p-8"
        data-testid="rsvp-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Digital RSVP</p>
            <h3 className="font-subheading text-xl uppercase tracking-wider">{event.title}</h3>
            <p className="font-body text-xs text-zinc-500 mt-1">{event.event_date} {event.venue && `- ${event.venue}`}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        <div className="border border-yellow-500/20 bg-yellow-500/5 p-4 mb-6">
          <p className="font-body text-xs text-yellow-400/80 leading-relaxed">
            This is not a purchase. This is a digital RSVP to be notified as soon as tickets become available. We will send you a link to purchase tickets when they go on sale.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Name</label>
            <input
              data-testid="rsvp-name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="bc-input w-full"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Email Address *</label>
            <input
              data-testid="rsvp-email"
              type="email"
              required
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="bc-input w-full"
              placeholder="your@email.com"
            />
          </div>

          {event.ticket_types && event.ticket_types.length > 0 && (
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">
                Ticket Interest (select all that apply)
              </label>
              <div className="flex flex-wrap gap-2">
                {event.ticket_types.map(type => (
                  <button
                    key={type}
                    type="button"
                    data-testid={`rsvp-ticket-${type.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => togglePref(type)}
                    className={`font-body text-xs px-4 py-2 border transition-all duration-200 ${
                      form.ticket_preferences.includes(type)
                        ? "border-white bg-white/10 text-white"
                        : "border-white/10 text-zinc-500 hover:border-white/30"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="rsvp-submit-btn"
            disabled={submitting}
            className="w-full font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Bell size={14} />
            {submitting ? "Submitting..." : "Notify Me When Tickets Drop"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function BuyTicketModal({ event, onClose }) {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [paymentTab, setPaymentTab] = useState("cashapp");

  useEffect(() => {
    axios.get(`${API}/events/${event.id}/ticket-types`).then(r => {
      setTicketTypes(r.data.types || []);
      if (r.data.types?.length) setSelectedType(r.data.types[0].name);
    }).catch(() => {});
  }, [event.id]);

  const selectedPrice = ticketTypes.find(t => t.name === selectedType)?.price || 0;
  const totalPrice = selectedPrice * quantity;

  const handlePurchase = async (method = "stripe") => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      // Purchase multiple tickets
      let lastTicket = null;
      for (let i = 0; i < quantity; i++) {
        const res = await axios.post(`${API}/tickets/purchase`, {
          event_id: event.id,
          attendee_name: form.name,
          attendee_email: form.email,
          ticket_type: selectedType,
          payment_method: method,
          origin_url: window.location.origin
        });
        lastTicket = res.data;
        // For Stripe, only first ticket creates checkout (covers total)
        if (res.data.payment_required && res.data.checkout_url && i === 0) {
          window.location.href = res.data.checkout_url;
          return;
        }
      }
      if (lastTicket?.pending_approval) {
        toast.success(`${quantity} ticket(s) reserved! Will be activated once payment is verified.`);
        window.location.href = `/tickets/success?ticket_id=${lastTicket.ticket.id}`;
      } else if (lastTicket?.ticket) {
        toast.success(`${quantity} ticket(s) issued!`);
        window.location.href = `/tickets/success?ticket_id=${lastTicket.ticket.id}`;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg border border-white/15 bg-zinc-950 overflow-y-auto max-h-[90vh]"
        data-testid="buy-ticket-modal"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-500">Get Tickets</p>
            <h3 className="font-subheading text-lg uppercase tracking-wider">{event.title}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Ticket Type Selection */}
          <div>
            <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Select Ticket</label>
            <div className="space-y-2">
              {ticketTypes.map(t => (
                <button key={t.name} onClick={() => setSelectedType(t.name)}
                  className={`w-full flex items-center justify-between p-4 border transition-all ${selectedType === t.name ? "border-white bg-white/5" : "border-white/10 hover:border-white/30"}`}
                >
                  <span className="font-subheading text-sm uppercase tracking-wider">{t.name}</span>
                  <span className="font-heading text-xl">{t.price > 0 ? `$${t.price}` : "FREE"}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">How Many Tickets?</label>
            <div className="flex items-center gap-4">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 border border-white/20 text-white text-lg flex items-center justify-center hover:bg-white/5 transition-colors">-</button>
              <span className="font-heading text-3xl w-12 text-center">{quantity}</span>
              <button onClick={() => setQuantity(Math.min(10, quantity + 1))} className="w-10 h-10 border border-white/20 text-white text-lg flex items-center justify-center hover:bg-white/5 transition-colors">+</button>
              {totalPrice > 0 && (
                <span className="font-body text-sm text-zinc-400 ml-4">Total: <span className="text-white font-semibold">${totalPrice.toFixed(2)}</span></span>
              )}
            </div>
          </div>

          {/* Attendee Info */}
          <div className="space-y-4">
            <input data-testid="ticket-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bc-input w-full" placeholder="Full name" />
            <input data-testid="ticket-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bc-input w-full" placeholder="Email address" />
          </div>

          {/* Payment Method */}
          {selectedPrice > 0 && (
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Payment Method</label>
              <div className="flex gap-2 mb-4">
                {[
                  { id: "cashapp", label: "Cash App" },
                  { id: "venmo", label: "Venmo" },
                ].map(m => (
                  <button key={m.id} onClick={() => setPaymentTab(m.id)}
                    className={`flex-1 font-subheading text-xs uppercase tracking-wider py-2 border transition-all ${paymentTab === m.id ? "border-white text-white" : "border-white/10 text-zinc-500"}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {paymentTab === "cashapp" && (
                <div className="text-center border border-white/10 p-6">
                  <p className="font-body text-sm text-zinc-400 mb-4">Send <span className="text-white font-semibold">${totalPrice.toFixed(2)}</span> via Cash App then click confirm</p>
                  <img src={CASHAPP_QR} alt="Cash App QR" className="w-40 h-40 mx-auto mb-4 bg-white p-2" />
                  <p className="font-body text-xs text-zinc-500 mb-4">$Badcompanyco</p>
                  <button onClick={() => handlePurchase("cashapp")} disabled={submitting}
                    className="w-full font-subheading text-xs uppercase tracking-widest bg-green-500/10 border border-green-500/30 text-green-400 px-6 py-3 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                  >
                    {submitting ? "Processing..." : "I've Sent Payment - Get My Ticket"}
                  </button>
                </div>
              )}

              {paymentTab === "venmo" && (
                <div className="text-center border border-white/10 p-6">
                  <p className="font-body text-sm text-zinc-400 mb-4">Send <span className="text-white font-semibold">${totalPrice.toFixed(2)}</span> via Venmo then click confirm</p>
                  <a href={VENMO_URL} target="_blank" rel="noopener noreferrer" className="inline-block mb-4 font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors">
                    Open Venmo
                  </a>
                  <button onClick={() => handlePurchase("venmo")} disabled={submitting}
                    className="w-full font-subheading text-xs uppercase tracking-widest bg-blue-500/10 border border-blue-500/30 text-blue-400 px-6 py-3 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                  >
                    {submitting ? "Processing..." : "I've Sent Payment - Get My Ticket"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Free ticket */}
          {selectedPrice === 0 && (
            <button onClick={() => handlePurchase("free")} disabled={submitting} data-testid="get-free-ticket-btn"
              className="w-full font-subheading text-sm uppercase tracking-widest bg-white text-black px-6 py-3.5 hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Ticket size={16} /> {submitting ? "Processing..." : "Get Free Ticket"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.includes("/api/uploads/")) {
    const filename = url.split("/api/uploads/").pop();
    return `/api/uploads/${filename}`;
  }
  if (url.startsWith("http://") && typeof window !== "undefined" && window.location.protocol === "https:") {
    return url.replace("http://", "https://");
  }
  return url;
}

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [buyTicketEvent, setBuyTicketEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => {
      setEvents(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const now = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.event_date >= now && e.active);
  const past = events.filter(e => e.event_date < now);

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 noise-overlay relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Experience</p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6"
          >
            Events
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed"
          >
            Join us at upcoming Bad Company events. RSVP to be first in line when tickets drop.
          </motion.p>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 lg:py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="font-subheading text-2xl sm:text-3xl uppercase tracking-tight flex items-center gap-3">
              <Calendar size={22} className="text-zinc-400" /> Upcoming Events
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map(i => (
                <div key={i} className="border border-white/5 animate-pulse">
                  <div className="aspect-video bg-zinc-900" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-zinc-900 w-1/3" />
                    <div className="h-6 bg-zinc-900 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <div className="border border-white/10 p-12 text-center">
              <Calendar size={32} className="text-zinc-600 mx-auto mb-4" />
              <p className="font-subheading text-lg uppercase tracking-wider text-zinc-500 mb-2">No Upcoming Events</p>
              <p className="font-body text-sm text-zinc-600">Check back soon for new Bad Company experiences</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upcoming.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15, duration: 0.6 }}
                  className="border border-white/10 overflow-hidden card-hover"
                  data-testid={`event-card-${i}`}
                >
                  {event.image_url && (
                    <div className="overflow-hidden">
                      <img src={resolveImageUrl(event.image_url)} alt={event.title} className="w-full object-contain" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="flex items-center gap-1 font-body text-xs text-zinc-500">
                        <Calendar size={12} /> {event.event_date}
                      </span>
                      {event.venue && (
                        <span className="flex items-center gap-1 font-body text-xs text-zinc-500">
                          <MapPin size={12} /> {event.venue}
                        </span>
                      )}
                    </div>
                    <h3 className="font-subheading text-xl uppercase tracking-wider mb-2">{event.title}</h3>
                    <p className="font-body text-sm text-zinc-500 mb-4 line-clamp-3">{event.description}</p>

                    {event.rsvp_count > 0 && (
                      <p className="font-body text-xs text-zinc-600 mb-4">
                        {event.rsvp_count} {event.rsvp_count === 1 ? "person" : "people"} interested
                      </p>
                    )}

                    {event.tickets_available && event.ticket_url ? (
                      <div className="flex items-center gap-3 flex-wrap">
                        <a
                          href={event.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-buy-tickets-${i}`}
                          className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors"
                        >
                          <Ticket size={14} /> Buy Tickets <ExternalLink size={12} />
                        </a>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/events`;
                            navigator.clipboard.writeText(url);
                            toast.success("Link copied! Paste it anywhere to share.");
                          }}
                          data-testid={`event-share-${i}`}
                          className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white hover:bg-white/5 transition-all"
                        >
                          <Share2 size={14} /> Copy Link to Share
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => setBuyTicketEvent(event)}
                          data-testid={`event-buy-${i}`}
                          className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors"
                        >
                          <Ticket size={14} /> Get Tickets
                        </button>
                        <button
                          onClick={() => setRsvpEvent(event)}
                          data-testid={`event-rsvp-${i}`}
                          className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white hover:bg-white/5 transition-all"
                        >
                          <Bell size={14} /> RSVP Only
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/events`;
                            navigator.clipboard.writeText(url);
                            toast.success("Link copied! Paste it anywhere to share.");
                          }}
                          data-testid={`event-share-${i}`}
                          className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white hover:bg-white/5 transition-all"
                        >
                          <Share2 size={14} /> Share
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Past Events */}
      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="font-subheading text-2xl uppercase tracking-tight flex items-center gap-3 text-zinc-500">
              <Clock size={20} /> Past Events
            </h2>
          </div>
          {past.length === 0 ? (
            <p className="font-body text-sm text-zinc-600">No past events yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {past.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="border border-white/5 overflow-hidden opacity-70"
                >
                  {event.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img src={resolveImageUrl(event.image_url)} alt={event.title} className="w-full h-full object-cover grayscale" />
                    </div>
                  )}
                  <div className="p-5">
                    <span className="font-body text-xs text-zinc-600">{event.event_date}</span>
                    <h3 className="font-subheading text-sm uppercase tracking-wider mt-1 text-zinc-400">{event.title}</h3>
                    {event.venue && <p className="font-body text-xs text-zinc-600 mt-1">{event.venue}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-zinc-950 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-body text-base text-zinc-500 mb-6">
            Want to sponsor an event or partner with Bad Company?
          </p>
          <a
            href="/get-involved"
            data-testid="events-cta-sponsor"
            className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-8 py-3.5 hover:border-white hover:bg-white/5 transition-all duration-300"
          >
            Partner With Us <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* RSVP Modal */}
      <AnimatePresence>
        {rsvpEvent && <RSVPModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} />}
      </AnimatePresence>
      <AnimatePresence>
        {buyTicketEvent && <BuyTicketModal event={buyTicketEvent} onClose={() => setBuyTicketEvent(null)} />}
      </AnimatePresence>
    </div>
  );
}
